import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Calendar, Star, User, Ticket, Gift, MapPin, Clock, 
  ChevronRight, QrCode, Download, ExternalLink, CalendarPlus,
  Sparkles, TrendingUp, Heart
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import ConsumerLayout from '@/components/ConsumerLayout';
import api from '@/lib/api';
import { format, parseISO, isPast, isFuture, isToday } from 'date-fns';

// Status badge component
const StatusBadge = ({ status }) => {
  const styles = {
    confirmed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    completed: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  };
  
  return (
    <Badge className={styles[status] || styles.pending}>
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </Badge>
  );
};

// Stat card component
const StatCard = ({ icon: Icon, label, value, sublabel, color = "indigo" }) => (
  <Card className="border-0 shadow-md">
    <CardContent className="p-6">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl bg-${color}-100 dark:bg-${color}-900/30 flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}-600 dark:text-${color}-400`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-foreground">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
          {sublabel && <p className="text-xs text-muted-foreground mt-1">{sublabel}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

// Reservation card component
const ReservationCard = ({ reservation }) => {
  const isUpcoming = isFuture(parseISO(reservation.date));
  const isCurrentDay = isToday(parseISO(reservation.date));
  
  return (
    <div 
      className={`p-4 rounded-xl border transition-all hover:shadow-md ${
        isCurrentDay ? 'border-green-500 bg-green-50 dark:bg-green-950/20' :
        isUpcoming ? 'border-indigo-200 dark:border-indigo-800' : 
        'border-gray-200 dark:border-gray-700 opacity-75'
      }`}
      data-testid={`reservation-${reservation.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            isCurrentDay ? 'bg-green-500 text-white' :
            isUpcoming ? 'bg-indigo-500 text-white' : 
            'bg-gray-300 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
          }`}>
            <Calendar className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-bold text-foreground">{reservation.venue_name}</h4>
            <p className="text-sm text-muted-foreground">
              {reservation.reservation_type === 'table' ? 'Table Reservation' : reservation.item_title || 'Booking'}
            </p>
          </div>
        </div>
        <StatusBadge status={reservation.status} />
      </div>
      
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div>
          <p className="text-muted-foreground">Date</p>
          <p className="font-semibold text-foreground">
            {isCurrentDay ? 'Today' : format(parseISO(reservation.date), 'MMM d, yyyy')}
          </p>
        </div>
        <div>
          <p className="text-muted-foreground">Time</p>
          <p className="font-semibold text-foreground">{reservation.time}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Party</p>
          <p className="font-semibold text-foreground">{reservation.party_size} guests</p>
        </div>
      </div>
      
      {reservation.special_requests && (
        <p className="mt-3 text-sm text-muted-foreground italic">
          "{reservation.special_requests}"
        </p>
      )}
      
      <div className="flex gap-2 mt-4">
        <Button variant="outline" size="sm" className="flex-1" asChild>
          <Link to={`/venues/${(reservation.venue_state || 'co').toLowerCase()}/${(reservation.venue_city || 'denver').toLowerCase()}/${reservation.venue_neighborhood || 'rino'}/${reservation.venue_slug || reservation.venue_id}`}>
            View Venue
          </Link>
        </Button>
        {isUpcoming && (
          <Button variant="outline" size="sm">
            <CalendarPlus className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Ticket card component
const TicketCard = ({ booking }) => {
  return (
    <div 
      className="p-4 rounded-xl border border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/20 dark:to-indigo-950/20"
      data-testid={`ticket-${booking.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <Ticket className="w-5 h-5 text-white" />
          </div>
          <div>
            <h4 className="font-bold text-foreground">{booking.item_title}</h4>
            <p className="text-sm text-muted-foreground">Event Ticket</p>
          </div>
        </div>
        <StatusBadge status={booking.status} />
      </div>
      
      <div className="grid grid-cols-2 gap-4 text-sm mb-4">
        <div>
          <p className="text-muted-foreground">Quantity</p>
          <p className="font-semibold text-foreground">{booking.quantity} ticket{booking.quantity > 1 ? 's' : ''}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Total Paid</p>
          <p className="font-semibold text-green-600">${booking.total_amount.toFixed(2)}</p>
        </div>
      </div>
      
      <div className="flex items-center justify-between p-3 bg-white/50 dark:bg-black/20 rounded-lg">
        <div className="flex items-center gap-2">
          <QrCode className="w-5 h-5 text-muted-foreground" />
          <span className="text-xs font-mono text-muted-foreground">
            {booking.id.slice(0, 8).toUpperCase()}
          </span>
        </div>
        <Button variant="ghost" size="sm">
          <Download className="w-4 h-4 mr-1" />
          Download
        </Button>
      </div>
    </div>
  );
};

// Voucher card component  
const VoucherCard = ({ booking }) => {
  return (
    <div 
      className="p-4 rounded-xl border-2 border-dashed border-amber-300 dark:border-amber-700 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20"
      data-testid={`voucher-${booking.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Gift className="w-5 h-5 text-white" />
          </div>
          <div>
            <h4 className="font-bold text-foreground">{booking.item_title}</h4>
            <p className="text-sm text-muted-foreground">Experience Voucher</p>
          </div>
        </div>
        <StatusBadge status={booking.status} />
      </div>
      
      <div className="grid grid-cols-2 gap-4 text-sm mb-4">
        <div>
          <p className="text-muted-foreground">Quantity</p>
          <p className="font-semibold text-foreground">{booking.quantity} voucher{booking.quantity > 1 ? 's' : ''}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Value</p>
          <p className="font-semibold text-green-600">${booking.total_amount.toFixed(2)}</p>
        </div>
      </div>
      
      <div className="p-3 bg-amber-900 dark:bg-amber-950 rounded-lg text-center">
        <p className="text-xs text-amber-200 mb-1">VOUCHER CODE</p>
        <p className="text-xl font-bold font-mono text-amber-400 tracking-widest">
          {booking.id.slice(0, 8).toUpperCase()}
        </p>
      </div>
      
      <p className="text-xs text-center text-muted-foreground mt-3">
        Present this code at the venue to redeem
      </p>
    </div>
  );
};

// Empty state component
const EmptyState = ({ icon: Icon, title, description, action, actionLabel }) => (
  <div className="text-center py-12">
    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
      <Icon className="w-8 h-8 text-gray-400" />
    </div>
    <h3 className="font-semibold text-foreground mb-2">{title}</h3>
    <p className="text-muted-foreground mb-4">{description}</p>
    {action && (
      <Button onClick={action} variant="outline">
        {actionLabel}
      </Button>
    )}
  </div>
);

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isGold, loading: authLoading, refreshUser } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const init = async () => {
      if (authLoading) return;
      
      if (!isAuthenticated) {
        navigate('/auth');
        return;
      }
      
      if (!user) {
        try {
          await refreshUser();
        } catch (error) {
          navigate('/auth');
          return;
        }
      }
      
      fetchAllData();
    };
    
    init();
  }, [isAuthenticated, authLoading, user]);

  const fetchAllData = async () => {
    try {
      const [bookingsRes, reservationsRes, itinerariesRes] = await Promise.all([
        api.get('/bookings'),
        api.get('/reservations'),
        api.get('/itineraries')
      ]);
      
      setBookings(bookingsRes.data);
      setReservations(reservationsRes.data);
      setItineraries(itinerariesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) return null;

  // Separate bookings by type
  const tickets = bookings.filter(b => b.item_type === 'event');
  const vouchers = bookings.filter(b => b.item_type === 'voucher');
  
  // Separate reservations
  const upcomingReservations = reservations.filter(r => 
    isFuture(parseISO(r.date)) || isToday(parseISO(r.date))
  ).sort((a, b) => new Date(a.date) - new Date(b.date));
  
  const pastReservations = reservations.filter(r => 
    isPast(parseISO(r.date)) && !isToday(parseISO(r.date))
  );

  // Calculate stats
  const totalSpent = bookings.reduce((sum, b) => sum + b.total_amount, 0);

  return (
    <ConsumerLayout hideFooter>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-1">
              Welcome back, {user?.full_name?.split(' ')[0]}! 👋
            </h1>
            <p className="text-muted-foreground">
              Manage your reservations, tickets, and experiences
            </p>
          </div>
          
          {isGold ? (
            <div className="mt-4 md:mt-0 flex items-center gap-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-4 py-2 rounded-full shadow-lg">
              <Star className="w-5 h-5 fill-current" />
              <span className="font-bold">Gold Member</span>
            </div>
          ) : (
            <Button 
              onClick={() => navigate('/gold-club')}
              className="mt-4 md:mt-0 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white"
              data-testid="upgrade-gold-btn"
            >
              <Star className="w-4 h-4 mr-2" />
              Upgrade to Gold
            </Button>
          )}
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            icon={Calendar} 
            label="Reservations" 
            value={upcomingReservations.length}
            sublabel="upcoming"
            color="indigo"
          />
          <StatCard 
            icon={Ticket} 
            label="Tickets" 
            value={tickets.length}
            sublabel="purchased"
            color="purple"
          />
          <StatCard 
            icon={Gift} 
            label="Vouchers" 
            value={vouchers.length}
            sublabel="to redeem"
            color="amber"
          />
          <StatCard 
            icon={TrendingUp} 
            label="Total Spent" 
            value={`$${totalSpent.toFixed(0)}`}
            color="green"
          />
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="reservations" data-testid="tab-reservations">
              Reservations {upcomingReservations.length > 0 && (
                <Badge variant="secondary" className="ml-2">{upcomingReservations.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="tickets" data-testid="tab-tickets">Tickets</TabsTrigger>
            <TabsTrigger value="vouchers" data-testid="tab-vouchers">Vouchers</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
              </div>
            ) : (
              <>
                {/* Upcoming Reservations */}
                <Card className="border-0 shadow-md">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-indigo-600" />
                        Upcoming Reservations
                      </CardTitle>
                      <CardDescription>Your scheduled visits</CardDescription>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab('reservations')}>
                      View all <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {upcomingReservations.length === 0 ? (
                      <EmptyState 
                        icon={Calendar}
                        title="No upcoming reservations"
                        description="Browse venues and book a table"
                        action={() => navigate('/venues')}
                        actionLabel="Browse Venues"
                      />
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {upcomingReservations.slice(0, 2).map(res => (
                          <ReservationCard key={res.id} reservation={res} />
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Tickets & Vouchers */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="border-0 shadow-md">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Ticket className="w-5 h-5 text-purple-600" />
                          Recent Tickets
                        </CardTitle>
                      </div>
                      {tickets.length > 0 && (
                        <Button variant="ghost" size="sm" onClick={() => setActiveTab('tickets')}>
                          View all <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                    </CardHeader>
                    <CardContent>
                      {tickets.length === 0 ? (
                        <EmptyState 
                          icon={Ticket}
                          title="No tickets yet"
                          description="Discover amazing events"
                          action={() => navigate('/events')}
                          actionLabel="Browse Events"
                        />
                      ) : (
                        <div className="space-y-4">
                          {tickets.slice(0, 2).map(ticket => (
                            <TicketCard key={ticket.id} booking={ticket} />
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-md">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Gift className="w-5 h-5 text-amber-600" />
                          Your Vouchers
                        </CardTitle>
                      </div>
                      {vouchers.length > 0 && (
                        <Button variant="ghost" size="sm" onClick={() => setActiveTab('vouchers')}>
                          View all <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                    </CardHeader>
                    <CardContent>
                      {vouchers.length === 0 ? (
                        <EmptyState 
                          icon={Gift}
                          title="No vouchers yet"
                          description="Explore unique experiences"
                          action={() => navigate('/experiences')}
                          actionLabel="Browse Experiences"
                        />
                      ) : (
                        <div className="space-y-4">
                          {vouchers.slice(0, 2).map(voucher => (
                            <VoucherCard key={voucher.id} booking={voucher} />
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* My Itineraries */}
                {itineraries.length > 0 && (
                  <Card className="border-0 shadow-md">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-indigo-600" />
                          My Itineraries
                        </CardTitle>
                        <CardDescription>Your planned nights out</CardDescription>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => navigate('/itinerary/new')}>
                        Create new <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {itineraries.slice(0, 3).map(itin => (
                          <Link 
                            key={itin.id} 
                            to={`/itinerary/${itin.id}`}
                            className="p-4 rounded-lg border hover:border-indigo-500 hover:shadow-md transition-all"
                          >
                            <h4 className="font-bold text-foreground mb-1">{itin.title}</h4>
                            <p className="text-sm text-muted-foreground mb-2">
                              {format(parseISO(itin.date), 'EEEE, MMM d')}
                            </p>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary">{itin.items?.length || 0} stops</Badge>
                              <Badge variant="outline">{itin.status}</Badge>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Reservations Tab */}
          <TabsContent value="reservations">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle>All Reservations</CardTitle>
                <CardDescription>
                  {upcomingReservations.length} upcoming · {pastReservations.length} past
                </CardDescription>
              </CardHeader>
              <CardContent>
                {reservations.length === 0 ? (
                  <EmptyState 
                    icon={Calendar}
                    title="No reservations"
                    description="Book a table at your favorite venue"
                    action={() => navigate('/venues')}
                    actionLabel="Browse Venues"
                  />
                ) : (
                  <div className="space-y-6">
                    {upcomingReservations.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-foreground mb-4">Upcoming</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {upcomingReservations.map(res => (
                            <ReservationCard key={res.id} reservation={res} />
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {pastReservations.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-muted-foreground mb-4">Past</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {pastReservations.map(res => (
                            <ReservationCard key={res.id} reservation={res} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tickets Tab */}
          <TabsContent value="tickets">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle>My Tickets</CardTitle>
                <CardDescription>{tickets.length} event tickets</CardDescription>
              </CardHeader>
              <CardContent>
                {tickets.length === 0 ? (
                  <EmptyState 
                    icon={Ticket}
                    title="No tickets"
                    description="Discover and book events in RiNo"
                    action={() => navigate('/events')}
                    actionLabel="Browse Events"
                  />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {tickets.map(ticket => (
                      <TicketCard key={ticket.id} booking={ticket} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vouchers Tab */}
          <TabsContent value="vouchers">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle>My Vouchers</CardTitle>
                <CardDescription>{vouchers.length} experience vouchers</CardDescription>
              </CardHeader>
              <CardContent>
                {vouchers.length === 0 ? (
                  <EmptyState 
                    icon={Gift}
                    title="No vouchers"
                    description="Explore unique experiences and gift cards"
                    action={() => navigate('/experiences')}
                    actionLabel="Browse Experiences"
                  />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {vouchers.map(voucher => (
                      <VoucherCard key={voucher.id} booking={voucher} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </ConsumerLayout>
  );
};

export default UserDashboard;
